import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CountryResource } from '@models/resources/country.model';
import { CultureResource } from '@models/resources/culture.model';
import { CurrencyResource } from '@models/resources/currency.model';
import { GenderResource } from '@models/resources/gender.model';
import { PaymentMethodResource } from '@models/resources/payment-method.model';
import { ProvinceStateResource } from '@models/resources/province-state.model';
import { SuffixResource } from '@models/resources/suffix.model';
import { TimeZoneResource } from '@models/resources/time-zones.model';
import { TitleResource } from '@models/resources/title.model';
import { ApiResponse } from '@models/responses/api-response.model';
import { EnvironmentService } from '@services/environment-service/environment.service';
import { Observable } from 'rxjs';

const enum ResourceType {
  Genders = 'genders',
  Countries = 'countries',
  Suffixes = 'suffixes',
  Cultures = 'cultures',
  Currencies = 'currencies',
  PaymentMethods = 'paymentmethods',
  ProvinceStates = 'provincestates',
  Titles = 'titles',
  PassengerTypes = 'passengertypes',
  TimeZones = 'timezones',
}

@Injectable({
  providedIn: 'root',
})
export class ResourceService {
  private static readonly CONTROLLER_ROOT = 'v1/resources';

  constructor(private httpClient: HttpClient) {}

  getGenders(culture?: string): Observable<ApiResponse<GenderResource[]>> {
    return this.getApiResources<GenderResource>(ResourceType.Genders, culture);
  }

  getCountries(culture?: string): Observable<ApiResponse<CountryResource[]>> {
    return this.getApiResources<CountryResource>(
      ResourceType.Countries,
      culture,
    );
  }

  getSuffixes(culture?: string): Observable<ApiResponse<SuffixResource[]>> {
    return this.getApiResources<SuffixResource>(ResourceType.Suffixes, culture);
  }

  getCultures(culture?: string): Observable<ApiResponse<CultureResource[]>> {
    return this.getApiResources<CultureResource>(
      ResourceType.Cultures,
      culture,
    );
  }

  getCurrencies(culture?: string): Observable<ApiResponse<CurrencyResource[]>> {
    return this.getApiResources<CurrencyResource>(
      ResourceType.Currencies,
      culture,
    );
  }

  getPaymentMethods(
    culture?: string,
  ): Observable<ApiResponse<PaymentMethodResource[]>> {
    return this.getApiResources<PaymentMethodResource>(
      ResourceType.PaymentMethods,
      culture,
    );
  }

  getProvinceStates(
    culture?: string,
  ): Observable<ApiResponse<ProvinceStateResource[]>> {
    return this.getApiResources<ProvinceStateResource>(
      ResourceType.ProvinceStates,
      culture,
    );
  }

  getTimeZones(culture?: string): Observable<ApiResponse<TimeZoneResource[]>> {
    return this.getApiResources<TimeZoneResource>(
      ResourceType.TimeZones,
      culture,
    );
  }

  getTitles(culture?: string): Observable<ApiResponse<TitleResource[]>> {
    return this.getApiResources<TitleResource>(ResourceType.Titles, culture);
  }

  private getApiResources<T>(
    name: ResourceType,
    culture?: string,
  ): Observable<ApiResponse<T[]>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${
      ResourceService.CONTROLLER_ROOT
    }/${name}${culture ? `?culture=${culture}` : ''}`;
    return this.httpClient.get<ApiResponse<T[]>>(endpoint);
  }
}
